import * as React from 'react'
import { clsx } from 'clsx'
import {
  FileTextIcon,
  MagnifyingGlassIcon,
  SpeakerLoudIcon,
  UploadIcon,
} from '@radix-ui/react-icons'

import {
  getBlockUpdateOverride,
  getValueBasedOnSelectedVariants,
  type MediaBlock,
  type Block,
  MediaBlockValueType,
  mediaBlockValueTypeSchema,
} from '@basehub/data/replicache/index'

import { useUploadFileToRepoMutation } from '~/hooks-data/use-upload-file-to-repo-mutation'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { Input, InputProps } from '~/sotano-kit/primitives/input'
import { getPlaceholder } from '../utils/get-placeholder'
import {
  BlockLabelMoreActionsPortal,
  useBlockInputBaseProps,
} from '../block-wrapper'
import { toast } from 'sonner'
import LoadingIcon from '../empty-state/loading-icon'
import {
  DEFAULT_MERGED_MEDIA_MIME_TYPES,
  allowsAnyFile,
  getDefaultMimeTypesByCategory,
  isValidTypeForCategory,
} from '@basehub/data/replicache/schema/block/valid-mime-types'
import { useEditorStore } from '../../editor-store'
import { useSelectedVariants } from '~/hooks/use-selected-variants'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import { UpdateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useTabsStore } from '../../tabs/store'
import { useEditorMode } from '../../editor-mode-provider'
import { widthAndHeightToAspectRatio } from '@basehub/utils/math/index'
import { Button } from '~/sotano-kit/primitives/button'
import { SelectMediaDialog } from './select-media-dialog'
import { formatBytes } from '~/lib/file-utils'
import { getSafeMediaTypeByConstraints } from './utils'

export const MediaBlockInput = ({
  block,
  parent,
  path,
  ...props
}: {
  block: MediaBlock
  parent: Block
  path: string
} & InputProps) => {
  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const repoId = data?.repoId
  const teamId = data?.team.id

  const inputRef = React.useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = React.useState(false)
  const baseProps = useBlockInputBaseProps(block)
  const previousBlob = React.useRef<string | null>(null)
  const videoElementRef = React.useRef<HTMLVideoElement>(null)
  const [recentlyUploadedVideo, setRecentlyUploadedVideo] = React.useState<{
    src: string
    aspectRatio?: string
    width: number
    height: number
  } | null>(null)

  const recentlyUploadedImage = useEditorStore((s) =>
    s.recentlyUploadedImages.get(block.id)
  )
  const setRecentlyUploadedImageById = useEditorStore(
    (s) => s.setRecentlyUploadedImageById
  )
  const setRecentlyUploadedImage = React.useCallback(
    (image: { width: number; height: number; src: string } | null) => {
      setRecentlyUploadedImageById(block.id, image)
    },
    [block.id, setRecentlyUploadedImageById]
  )

  // Cuando el componente se desmonte o cambie el video:
  React.useEffect(() => {
    return () => {
      if (previousBlob.current) {
        URL.revokeObjectURL(previousBlob.current)
      }
    }
  }, [])

  const {
    mutateAsync,
    isSuccess: isSuccessUploadFile,
    isLoading: isLoadingUploadFile,
  } = useUploadFileToRepoMutation()

  const undoManager = useRepoUndoManager()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const [fileToRetry, setFileToRetry] = React.useState<File | null>(null)

  const selectedDashboardVariants = useSelectedVariants({
    parentBlock: parent,
    block,
  })

  const handleChange = React.useCallback(
    async (e: { target: { files: FileList | File[] | null } }) => {
      if (!teamId || !repoId) return
      const file = e.target.files?.[0]
      if (!file || !replicache) return

      // here clean all the previous states, ALL
      if (recentlyUploadedImage) {
        setRecentlyUploadedImage(null)
      }
      if (recentlyUploadedVideo) {
        setRecentlyUploadedVideo(null)
      }
      if (previousBlob.current) {
        URL.revokeObjectURL(previousBlob.current)
      }
      previousBlob.current = null

      if (
        !isValidTypeForCategory(
          file.type,
          Object.keys(
            block.constraints.allowedMimeTypes ?? {}
          ) as MediaBlockValueType[]
        )
      ) {
        inputRef.current?.value && (inputRef.current.value = '')
        return toast.error(`Invalid file type: ${file.type}`)
      }

      const mimeTypeCategory = getSafeMediaTypeByConstraints(
        file.type,
        block.constraints.allowedMimeTypes
      )
      if (!mimeTypeCategory) {
        inputRef.current?.value && (inputRef.current.value = '')
        return toast.error(
          `File type "${file.type}" not allowed in this block`,
          {
            action: {
              label: 'Add to allowed formats',
              onClick: async () => {
                let category: MediaBlockValueType
                // exceptions
                if (file.type.includes('adobe.photoshop')) {
                  category = 'file'
                } else {
                  category = mediaBlockValueTypeSchema
                    .catch('file')
                    .parse(file.type.split('/')[0])
                }

                let previous = block.constraints.allowedMimeTypes?.[category]
                if (previous === true) {
                  previous = getDefaultMimeTypesByCategory(category)
                } else if (!Array.isArray(previous)) {
                  previous = []
                }

                await replicache.mutate.updateBlock({
                  id: block.id,
                  strategy: 'merge-deep',
                  path,
                  instanceChildTarget: block.instanceChildTarget,
                  isoNow: new Date().toISOString(),
                  data: {
                    constraints: {
                      allowedMimeTypes: {
                        [category]: [...previous, file.type],
                      },
                    },
                  },
                })

                setFileToRetry(file)
              },
            },
          }
        )
      }

      const onLoadVariants = [...selectedDashboardVariants]

      switch (mimeTypeCategory) {
        case 'image': {
          const timeout = setTimeout(() => {
            toast.error(
              `Error uploading image. This may happen if the file is an SVG which is malformed. Contact us and we'll help you out.`
            )
          }, 3000)

          const image = new Image()
          image.onload = async () => {
            clearTimeout(timeout)
            setRecentlyUploadedImage({
              src: image.src,
              width: image.width,
              height: image.height,
            })

            await replicache.mutate.updateBlock({
              id: block.id,
              instanceChildTarget: block.instanceChildTarget,
              data: getBlockUpdateOverride({
                newValue: { status: 'uploading' },
                block,
                selectedVariants: onLoadVariants,
              }),
              path,
              strategy: 'merge',
              isoNow: new Date().toISOString(),
            })

            try {
              const { uploadURL } = await mutateAsync({
                repoId,
                teamId,
                file,
              })

              const params: UpdateBlockParams = {
                id: block.id,
                instanceChildTarget: block.instanceChildTarget,
                data: getBlockUpdateOverride({
                  newValue: {
                    type: 'image',
                    status: 'uploaded',
                    mimeType: file.type,
                    fileName: file.name,
                    fileSize: file.size,
                    width: image.width,
                    height: image.height,
                    url: uploadURL,
                    cloudflareImage: {
                      uploadState: 'will-not-upload',
                      url: null,
                      reason: 'deprecated',
                    },
                    altText: null,
                    lastModified: file.lastModified,
                  } satisfies MediaBlock['value'],
                  block,
                  selectedVariants: onLoadVariants,
                }),
                path,
                strategy: 'merge',
                isoNow: new Date().toISOString(),
              }
              await replicache.mutate.updateBlock(params)
              undoManager.current?.push({
                redo: [
                  {
                    op: 'focus',
                    args: {
                      preferredTabKey: activeTabKey,
                      focus: {
                        type: 'block:value',
                        blockId: idToSourceBlockId({
                          id: block.id,
                          sourceBlockId: block.sourceBlockId,
                        }),
                      },
                    },
                  },
                  { op: 'updateBlock', args: params },
                ],
                undo: [
                  {
                    op: 'focus',
                    args: {
                      preferredTabKey: activeTabKey,
                      focus: {
                        type: 'block:value',
                        blockId: idToSourceBlockId({
                          id: block.id,
                          sourceBlockId: block.sourceBlockId,
                        }),
                      },
                    },
                  },
                  {
                    op: 'updateBlock',
                    args: {
                      id: block.id,
                      instanceChildTarget: block.instanceChildTarget,
                      data: {
                        type: block.type,
                        value: { status: 'idle' },
                      },
                      path,
                      strategy: 'merge',
                      isoNow: new Date().toISOString(),
                    },
                  },
                ],
              })
            } catch (error) {
              console.error(error)
              await replicache.mutate.updateBlock({
                id: block.id,
                instanceChildTarget: block.instanceChildTarget,
                data: getBlockUpdateOverride({
                  newValue: { status: 'idle' },
                  block,
                  selectedVariants: onLoadVariants,
                }),
                path,
                strategy: 'merge',
                isoNow: new Date().toISOString(),
              })
              toast.error('Image could not be uploaded')
            }

            setRecentlyUploadedImage(null)
          }

          image.src = URL.createObjectURL(file)
          break
        }
        case 'audio': {
          await replicache.mutate.updateBlock({
            id: block.id,
            data: getBlockUpdateOverride({
              newValue: { status: 'uploading' },
              block,
              selectedVariants: onLoadVariants,
            }),
            instanceChildTarget: block.instanceChildTarget,
            path,
            strategy: 'merge',
            isoNow: new Date().toISOString(),
          })

          try {
            const { uploadURL } = await mutateAsync({
              repoId,
              teamId,
              file,
            })

            // Create a temporary audio element to get the duration
            const tempAudio = new Audio(uploadURL)
            await new Promise((resolve, reject) => {
              tempAudio.addEventListener('loadedmetadata', () => {
                resolve(null)
              })
              tempAudio.addEventListener('error', () => {
                reject(new Error('Failed to load audio metadata'))
              })
            })

            const params: UpdateBlockParams = {
              id: block.id,
              data: getBlockUpdateOverride({
                newValue: {
                  type: 'audio',
                  status: 'uploaded',
                  url: uploadURL,
                  mimeType: file.type,
                  fileName: file.name,
                  fileSize: file.size,
                  lastModified: file.lastModified,
                  duration: Number.isNaN(tempAudio.duration)
                    ? null
                    : tempAudio.duration,
                } satisfies MediaBlock['value'],
                block,
                selectedVariants: onLoadVariants,
              }),
              path,
              instanceChildTarget: block.instanceChildTarget,
              strategy: 'merge',
              isoNow: new Date().toISOString(),
            }
            await replicache.mutate.updateBlock(params)
            undoManager.current?.push({
              redo: [
                {
                  op: 'focus',
                  args: {
                    focus: {
                      type: 'block:value',
                      blockId: idToSourceBlockId({
                        id: block.id,
                        sourceBlockId: block.sourceBlockId,
                      }),
                    },
                    preferredTabKey: activeTabKey,
                  },
                },
                { op: 'updateBlock', args: params },
              ],
              undo: [
                {
                  op: 'focus',
                  args: {
                    focus: {
                      type: 'block:value',
                      blockId: idToSourceBlockId({
                        id: block.id,
                        sourceBlockId: block.sourceBlockId,
                      }),
                    },
                    preferredTabKey: activeTabKey,
                  },
                },
                {
                  op: 'updateBlock',
                  args: {
                    id: block.id,
                    data: {
                      type: block.type,
                      value: { status: 'idle' },
                    },
                    path,
                    strategy: 'merge',
                    instanceChildTarget: block.instanceChildTarget,
                    isoNow: new Date().toISOString(),
                  },
                },
              ],
            })
          } catch (error) {
            await replicache.mutate.updateBlock({
              id: block.id,
              data: getBlockUpdateOverride({
                newValue: { status: 'idle' },
                block,
                selectedVariants: onLoadVariants,
              }),
              path,
              strategy: 'merge',
              instanceChildTarget: block.instanceChildTarget,
              isoNow: new Date().toISOString(),
            })
            toast.error('Audio could not be uploaded')
          }
          break
        }
        case 'file': {
          await replicache.mutate.updateBlock({
            id: block.id,
            instanceChildTarget: block.instanceChildTarget,
            data: getBlockUpdateOverride({
              newValue: { status: 'uploading' },
              block,
              selectedVariants: onLoadVariants,
            }),
            path,
            strategy: 'merge',
            isoNow: new Date().toISOString(),
          })

          try {
            const { uploadURL } = await mutateAsync({
              repoId,
              teamId,
              file,
            })
            const params: UpdateBlockParams = {
              id: block.id,
              instanceChildTarget: block.instanceChildTarget,
              data: getBlockUpdateOverride({
                block,
                selectedVariants: onLoadVariants,
                newValue: {
                  type: 'file',
                  status: 'uploaded',
                  url: uploadURL,
                  mimeType: file.type,
                  fileName: file.name,
                  fileSize: file.size,
                  lastModified: file.lastModified,
                } satisfies MediaBlock['value'],
              }),
              path,
              strategy: 'merge',
              isoNow: new Date().toISOString(),
            }
            await replicache.mutate.updateBlock(params)
            undoManager.current?.push({
              redo: [
                {
                  op: 'focus',
                  args: {
                    preferredTabKey: activeTabKey,
                    focus: {
                      type: 'block:value',
                      blockId: idToSourceBlockId({
                        id: block.id,
                        sourceBlockId: block.sourceBlockId,
                      }),
                    },
                  },
                },
                { op: 'updateBlock', args: params },
              ],
              undo: [
                {
                  op: 'focus',
                  args: {
                    preferredTabKey: activeTabKey,
                    focus: {
                      type: 'block:value',
                      blockId: idToSourceBlockId({
                        id: block.id,
                        sourceBlockId: block.sourceBlockId,
                      }),
                    },
                  },
                },
                {
                  op: 'updateBlock',
                  args: {
                    id: block.id,
                    instanceChildTarget: block.instanceChildTarget,
                    data: {
                      type: block.type,
                      value: { status: 'idle' },
                    },
                    path,
                    strategy: 'merge',
                    isoNow: new Date().toISOString(),
                  },
                },
              ],
            })
          } catch (error) {
            console.error(error)
            await replicache.mutate.updateBlock({
              id: block.id,
              instanceChildTarget: block.instanceChildTarget,
              data: getBlockUpdateOverride({
                block,
                selectedVariants: onLoadVariants,
                newValue: { status: 'idle' },
              }),
              path,
              strategy: 'merge',
              isoNow: new Date().toISOString(),
            })
            toast.error('File could not be uploaded')
          }
          break
        }
        case 'video':
          {
            if (!videoElementRef.current) return
            await replicache.mutate.updateBlock({
              strategy: 'merge',
              id: block.id,
              data: getBlockUpdateOverride({
                block,
                selectedVariants: onLoadVariants,
                newValue: { status: 'uploading' },
              }),
              path,
              instanceChildTarget: block.instanceChildTarget,
              isoNow: new Date().toISOString(),
            })

            const src = URL.createObjectURL(file)
            if (previousBlob.current) {
              URL.revokeObjectURL(previousBlob.current)
            }
            previousBlob.current = src
            const handleLoadedMetadata = async () => {
              if (!videoElementRef.current) return

              setRecentlyUploadedVideo({
                src,
                aspectRatio: widthAndHeightToAspectRatio(
                  videoElementRef.current.videoWidth,
                  videoElementRef.current.videoHeight
                ),
                width: videoElementRef.current.videoWidth,
                height: videoElementRef.current.videoHeight,
              })

              try {
                const { uploadURL } = await mutateAsync({
                  repoId,
                  teamId,
                  file,
                })
                const params: UpdateBlockParams = {
                  strategy: 'merge',
                  id: block.id,
                  instanceChildTarget: block.instanceChildTarget,
                  data: getBlockUpdateOverride({
                    block,
                    selectedVariants: onLoadVariants,
                    newValue: {
                      type: 'video',
                      status: 'uploaded',
                      url: uploadURL,
                      mimeType: file.type,
                      fileName: file.name,
                      fileSize: file.size,
                      width: videoElementRef.current.videoWidth,
                      height: videoElementRef.current.videoHeight,
                      lastModified: file.lastModified,
                      duration: Number.isNaN(videoElementRef.current.duration)
                        ? null
                        : videoElementRef.current.duration,
                    } satisfies MediaBlock['value'],
                  }),
                  path,
                  isoNow: new Date().toISOString(),
                }
                await replicache.mutate.updateBlock(params)
                undoManager.current?.push({
                  redo: [
                    {
                      op: 'focus',
                      args: {
                        focus: {
                          type: 'block:value',
                          blockId: idToSourceBlockId({
                            id: block.id,
                            sourceBlockId: block.sourceBlockId,
                          }),
                        },
                        preferredTabKey: activeTabKey,
                      },
                    },
                    { op: 'updateBlock', args: params },
                  ],
                  undo: [
                    {
                      op: 'focus',
                      args: {
                        focus: {
                          type: 'block:value',
                          blockId: idToSourceBlockId({
                            id: block.id,
                            sourceBlockId: block.sourceBlockId,
                          }),
                        },
                        preferredTabKey: activeTabKey,
                      },
                    },
                    {
                      op: 'updateBlock',
                      args: {
                        id: block.id,
                        instanceChildTarget: block.instanceChildTarget,
                        data: {
                          type: block.type,
                          value: { status: 'idle' },
                        },
                        path,
                        strategy: 'merge',
                        isoNow: new Date().toISOString(),
                      },
                    },
                  ],
                })
              } catch (error) {
                await replicache.mutate.updateBlock({
                  strategy: 'merge',
                  id: block.id,
                  instanceChildTarget: block.instanceChildTarget,
                  data: getBlockUpdateOverride({
                    block,
                    selectedVariants: onLoadVariants,
                    newValue: { status: 'idle' },
                  }),
                  path,
                  isoNow: new Date().toISOString(),
                })
                toast.error('Video could not be uploaded')
              } finally {
                URL.revokeObjectURL(src)
                videoElementRef.current.src = ''
                videoElementRef.current.load()
              }

              setRecentlyUploadedVideo(null)
            }
            videoElementRef.current.removeEventListener(
              'loadedmetadata',
              handleLoadedMetadata
            )
            videoElementRef.current.addEventListener(
              'loadedmetadata',
              handleLoadedMetadata,
              { once: true }
            )

            if (videoElementRef.current) {
              videoElementRef.current.src = src
              videoElementRef.current.load()
            }
          }
          inputRef.current?.value && (inputRef.current.value = '')
      }
    },
    [
      teamId,
      repoId,
      replicache,
      recentlyUploadedImage,
      recentlyUploadedVideo,
      block,
      selectedDashboardVariants,
      setRecentlyUploadedImage,
      path,
      mutateAsync,
      undoManager,
      activeTabKey,
    ]
  )

  React.useEffect(() => {
    if (fileToRetry) {
      // Create synthetic event with the saved file
      const syntheticEvent = { target: { files: [fileToRetry] } }
      handleChange(syntheticEvent)
      setFileToRetry(null)
    }
  }, [fileToRetry, handleChange])

  const value = React.useMemo(
    () =>
      getValueBasedOnSelectedVariants({
        block,
        variantsToFilterBy: selectedDashboardVariants,
      }),
    [block, selectedDashboardVariants]
  )

  const displayedImage: null | {
    src: string
    width: number
    height: number
    aspectRatio: number
  } = React.useMemo(() => {
    if (recentlyUploadedImage) {
      return {
        src: recentlyUploadedImage.src,
        aspectRatio: recentlyUploadedImage.width / recentlyUploadedImage.height,
        width: recentlyUploadedImage.width,
        height: recentlyUploadedImage.height,
      }
    }

    if (value.status === 'uploaded' && value.type === 'image') {
      return {
        src: value.url,
        aspectRatio: value.width / value.height,
        width: value.width,
        height: value.height,
      }
    }
    return null
  }, [value, recentlyUploadedImage])

  const isLoading = React.useMemo(
    () => isLoadingUploadFile || Boolean(recentlyUploadedImage),
    [isLoadingUploadFile, recentlyUploadedImage]
  )
  const editorMode = useEditorMode()
  const disabled = isLoading || editorMode === 'read'
  const baseInputClassName =
    'border-dashed text-opacity-0 file:hidden focus:border-solid'

  const displayedVideo = React.useMemo(() => {
    if (recentlyUploadedVideo) return recentlyUploadedVideo

    if (value.status === 'uploaded' && value.type === 'video') {
      return {
        src: value.url,
        aspectRatio: widthAndHeightToAspectRatio(value.width, value.height),
        width: value.width,
        height: value.height,
      }
    }

    return null
  }, [value, recentlyUploadedVideo])

  const acceptedMimeTypes = React.useMemo(() => {
    if (allowsAnyFile(block.constraints.allowedMimeTypes)) {
      return '*/*'
    }
    return block.constraints.allowedMimeTypes === null
      ? DEFAULT_MERGED_MEDIA_MIME_TYPES.join(',')
      : (
          Object.entries(block.constraints.allowedMimeTypes)?.map(
            ([cat, t]) => {
              if (!t) return null
              if (t === true) {
                return getDefaultMimeTypesByCategory(
                  cat as MediaBlockValueType
                ).join(',')
              }
              return t.join(',')
            }
          ) satisfies (string | null)[]
        )
          .filter(Boolean)
          .join(',')
  }, [block.constraints.allowedMimeTypes])

  if (value.status === 'uploaded' && !isLoading && value.type === 'audio') {
    return (
      <div className="relative p-px">
        <Input
          {...baseProps}
          disabled={disabled}
          name={block.title}
          placeholder={getPlaceholder(block.title)}
          className="user-select-none pointer-events-none absolute !h-full inset-0 text-opacity-0 file:hidden border-dashed focus:border-solid"
          onChange={handleChange}
          accept={acceptedMimeTypes}
          ref={inputRef}
          {...props}
        />
        <audio
          src={value.url}
          className="w-full borderless cursor-default rounded-md !bg-dark-base-dark"
          controls
          tabIndex={-1}
        />
      </div>
    )
  }

  if (value.status === 'uploaded' && !isLoading && value.type === 'file') {
    return (
      <div className="relative">
        <Input
          {...baseProps}
          disabled={disabled}
          name={block.title}
          placeholder={getPlaceholder(block.title)}
          className="user-select-none pointer-events-none !h-full absolute inset-0 file:hidden border-dashed focus:border-solid text-opacity-0 !bg-dark-base-dark"
          onChange={handleChange}
          ref={inputRef}
          accept={acceptedMimeTypes}
          {...props}
        />

        <div
          className="flex items-center space-x-4 rounded-lg py-2 pl-4 pr-3 relative"
          onDoubleClick={() => {
            inputRef.current?.click()
          }}
        >
          {value.type === 'file' ? (
            <FileTextIcon className="inline-block" width={20} height={20} />
          ) : (
            <SpeakerLoudIcon className="inline-block" width={20} height={20} />
          )}
          <div className="inline-block">
            <p className="text-xs font-medium">{value.fileName}</p>
            <p className="text-xxs text-dark-faint">
              {formatBytes(value.fileSize)}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <BlockLabelMoreActionsPortal blockId={block.id} visible>
        {isLoading && (
          <FileLoadingIndicator
            label={isSuccessUploadFile ? 'Optimizing' : undefined}
          />
        )}
      </BlockLabelMoreActionsPortal>
      <div className="relative">
        {displayedVideo?.src ? (
          <div className="w-fit max-w-full overflow-hidden rounded-md border border-dashed border-dark-border-solid bg-dark-base-dark p-2">
            <Input
              {...baseProps}
              placeholder={getPlaceholder(block.title)}
              disabled={disabled}
              className={clsx(
                baseInputClassName,
                'user-select-none pointer-events-none absolute !h-full inset-0'
              )}
              onChange={handleChange}
              accept={acceptedMimeTypes}
              ref={inputRef}
              {...props}
            />
            <div
              style={{
                aspectRatio: displayedVideo.aspectRatio,
                width: displayedVideo.width,
                maxWidth: '100%',
              }}
              className="relative"
            >
              <video
                key={displayedVideo.src} // nueva key cada vez que cambia la src
                src={displayedVideo.src}
                style={{
                  backgroundImage: `url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAMPmlDQ1BJQ0MgUHJvZmlsZQAASImVVwdYU8kWnluSkEBooUsJvQkiNYCUEFoA6V1UQhIglBgDQcVeFhVcu1jAhq6KKFhpFhRRLCyKvS8WVJR1sWBX3qSArvvK9+b75s5//znznzPnztx7BwC1ExyRKA9VByBfWCiODQmgJ6ek0klPAQ5QoAurHYdbIGJGR0cAWIbav5d31wEiba84SLX+2f9fiwaPX8AFAImGOINXwM2H+BAAeCVXJC4EgCjlzacUiqQYVqAlhgFCvEiKs+S4Uooz5HifzCY+lgVxGwBKKhyOOAsA1UuQpxdxs6CGaj/ETkKeQAiAGh1i3/z8STyI0yG2gTYiiKX6jIwfdLL+ppkxrMnhZA1j+VxkRSlQUCDK40z7P9Pxv0t+nmTIhxWsKtni0FjpnGHebuZOCpdiFYj7hBmRURBrQvxBwJPZQ4xSsiWhCXJ71JBbwII5AzoQO/E4geEQG0IcLMyLjFDwGZmCYDbEcIWgUwWF7HiI9SBexC8IilPYbBFPilX4QuszxSymgj/LEcv8Sn3dl+QmMBX6r7P5bIU+plqcHZ8EMQViiyJBYiTEqhA7FuTGhStsxhRnsyKHbMSSWGn8FhDH8oUhAXJ9rChTHByrsC/NLxiaL7YlW8COVOADhdnxofL8YG1cjix+OBfsEl/ITBjS4RckRwzNhccPDJLPHXvGFybEKXQ+iAoDYuVjcYooL1phj5vx80KkvBnErgVFcYqxeGIhXJByfTxTVBgdL48TL87hhEXL48GXgwjAAoGADiSwZoBJIAcIOvsa+uCdvCcYcIAYZAE+cFAwQyOSZD1CeI0DxeBPiPigYHhcgKyXD4og/3WYlV8dQKast0g2Ihc8gTgfhIM8eC+RjRIOe0sEjyEj+Id3DqxcGG8erNL+f88Psd8ZJmQiFIxkyCNdbciSGEQMJIYSg4m2uAHui3vjEfDqD6szzsA9h+bx3Z7whNBFeEi4Rugm3JoomCf+KcqxoBvqBytykfFjLnArqOmGB+A+UB0q4zq4AXDAXaEfJu4HPbtBlqWIW5oV+k/af5vBD09DYUd2IqNkXbI/2ebnkap2qm7DKtJc/5gfeawZw/lmDff87J/1Q/Z5sA3/2RJbhB3E2rGT2DnsKNYA6FgL1oh1YMekeHh1PZatriFvsbJ4cqGO4B/+hp6sNJMFTjVOvU5f5H2F/KnSdzRgTRJNEwuysgvpTPhF4NPZQq7jSLqzk7MLANLvi/z19SZG9t1AdDq+c/P/AMCnZXBw8Mh3LqwFgP0ecPs3fedsGPDToQzA2SauRFwk53DphQDfEmpwp+kDY2AObOB8nIE78Ab+IAiEgSgQD1LABBh9NlznYjAFzABzQQkoA8vBGrABbAbbwC6wFxwADeAoOAnOgAvgErgG7sDV0wNegH7wDnxGEISEUBEaoo+YIJaIPeKMMBBfJAiJQGKRFCQdyUKEiASZgcxHypCVyAZkK1KN7EeakJPIOaQLuYU8QHqR18gnFENVUC3UCLVCR6EMlImGo/HoeDQLnYwWowvQpeg6tArdg9ajJ9EL6DW0G32BDmAAU8Z0MFPMAWNgLCwKS8UyMTE2CyvFyrEqrBZrhs/5CtaN9WEfcSJOw+m4A1zBoXgCzsUn47PwJfgGfBdej7fhV/AHeD/+jUAlGBLsCV4ENiGZkEWYQighlBN2EA4TTsO91EN4RyQSdYjWRA+4F1OIOcTpxCXEjcQ64gliF/ERcYBEIumT7Ek+pCgSh1RIKiGtJ+0htZAuk3pIH5SUlUyUnJWClVKVhErzlMqVdisdV7qs9FTpM1mdbEn2IkeReeRp5GXk7eRm8kVyD/kzRYNiTfGhxFNyKHMp6yi1lNOUu5Q3ysrKZsqeyjHKAuU5yuuU9ymfVX6g/FFFU8VOhaWSpiJRWaqyU+WEyi2VN1Qq1YrqT02lFlKXUqupp6j3qR9UaaqOqmxVnups1QrVetXLqi/VyGqWaky1CWrFauVqB9UuqvWpk9Wt1FnqHPVZ6hXqTeo31Ac0aBqjNaI08jWWaOzWOKfxTJOkaaUZpMnTXKC5TfOU5iMaRjOnsWhc2nzadtppWo8WUctai62Vo1WmtVerU6tfW1PbVTtRe6p2hfYx7W4dTMdKh62Tp7NM54DOdZ1Puka6TF2+7mLdWt3Luu/1Ruj56/H1SvXq9K7pfdKn6wfp5+qv0G/Qv2eAG9gZxBhMMdhkcNqgb4TWCO8R3BGlIw6MuG2IGtoZxhpON9xm2GE4YGRsFGIkMlpvdMqoz1jH2N84x3i18XHjXhOaia+JwGS1SYvJc7o2nUnPo6+jt9H7TQ1NQ00lpltNO00/m1mbJZjNM6szu2dOMWeYZ5qvNm8177cwsRhrMcOixuK2JdmSYZltuday3fK9lbVVktVCqwarZ9Z61mzrYusa67s2VBs/m8k2VTZXbYm2DNtc2422l+xQOze7bLsKu4v2qL27vcB+o33XSMJIz5HCkVUjbzioODAdihxqHB446jhGOM5zbHB8OcpiVOqoFaPaR31zcnPKc9rudGe05uiw0fNGN49+7WznzHWucL7qQnUJdpnt0ujyytXele+6yfWmG81trNtCt1a3r+4e7mL3WvdeDwuPdI9KjxsMLUY0YwnjrCfBM8BztudRz49e7l6FXge8/vJ28M713u39bIz1GP6Y7WMe+Zj5cHy2+nT70n3Tfbf4dvuZ+nH8qvwe+pv78/x3+D9l2jJzmHuYLwOcAsQBhwPes7xYM1knArHAkMDSwM4gzaCEoA1B94PNgrOCa4L7Q9xCpoecCCWEhoeuCL3BNmJz2dXs/jCPsJlhbeEq4XHhG8IfRthFiCOax6Jjw8auGns30jJSGNkQBaLYUaui7kVbR0+OPhJDjImOqYh5Ejs6dkZsexwtbmLc7rh38QHxy+LvJNgkSBJaE9US0xKrE98nBSatTOpOHpU8M/lCikGKIKUxlZSamLojdWBc0Lg143rS3NJK0q6Ptx4/dfy5CQYT8iYcm6g2kTPxYDohPSl9d/oXThSnijOQwc6ozOjnsrhruS94/rzVvF6+D38l/2mmT+bKzGdZPlmrsnqz/bLLs/sELMEGwauc0JzNOe9zo3J35g7mJeXV5Svlp+c3CTWFucK2ScaTpk7qEtmLSkTdk70mr5ncLw4X7yhACsYXNBZqwR/5DomN5BfJgyLfooqiD1MSpxycqjFVOLVjmt20xdOeFgcX/zYdn86d3jrDdMbcGQ9mMmdunYXMypjVOtt89oLZPXNC5uyaS5mbO/f3eU7zVs57Oz9pfvMCowVzFjz6JeSXmhLVEnHJjYXeCzcvwhcJFnUudlm8fvG3Ul7p+TKnsvKyL0u4S87/OvrXdb8OLs1c2rnMfdmm5cTlwuXXV/it2LVSY2Xxykerxq6qX01fXbr67ZqJa86Vu5ZvXktZK1nbvS5iXeN6i/XL13/ZkL3hWkVARV2lYeXiyvcbeRsvb/LfVLvZaHPZ5k9bBFtubg3ZWl9lVVW+jbitaNuT7Ynb239j/Fa9w2BH2Y6vO4U7u3fF7mqr9qiu3m24e1kNWiOp6d2TtufS3sC9jbUOtVvrdOrK9oF9kn3P96fvv34g/EDrQcbB2kOWhyoP0w6X1iP10+r7G7IbuhtTGruawppam72bDx9xPLLzqOnRimPax5YdpxxfcHywpbhl4IToRN/JrJOPWie23jmVfOpqW0xb5+nw02fPBJ851c5sbznrc/boOa9zTecZ5xsuuF+o73DrOPy72++HO9076y96XGy85HmpuWtM1/HLfpdPXgm8cuYq++qFa5HXuq4nXL95I+1G903ezWe38m69ul10+/OdOXcJd0vvqd8rv294v+oP2z/qut27jz0IfNDxMO7hnUfcRy8eFzz+0rPgCfVJ+VOTp9XPnJ8d7Q3uvfR83POeF6IXn/tK/tT4s/KlzctDf/n/1dGf3N/zSvxq8PWSN/pvdr51fds6ED1w/13+u8/vSz/of9j1kfGx/VPSp6efp3whfVn31fZr87fwb3cH8wcHRRwxR/YrgMGKZmYC8HonANQUAGjwfEYZJz//yQoiP7PKEPhPWH5GlBV3AGrh/3tMH/y7uQHAvu3w+AX11dIAiKYCEO8JUBeX4Tp0VpOdK6WFCM8BW2K/ZuRngH9T5GfOH+L+uQVSVVfwc/sv6N18VQn/l7wAAAA4ZVhJZk1NACoAAAAIAAGHaQAEAAAAAQAAABoAAAAAAAKgAgAEAAAAAQAAACCgAwAEAAAAAQAAACAAAAAAj05AyQAAAFhJREFUWAntlkEKACAIBDV8Rf//p9UD1EOUlwk6LQvL5EoqInPf8Lh7qB1BVVO98o/U/UEkAAQgYFVPb3te+XkCCECgnYBVPX29J9oJEAACEOA/wAxAAAIL9Q4YXZojZ+kAAAAASUVORK5CYII=)`,
                  backgroundRepeat: 'repeat',
                  backgroundSize: '16px',
                }}
                controls
                className={clsx('h-full w-full', {
                  'pointer-events-none select-none opacity-30': isLoading,
                })}
              />
            </div>
          </div>
        ) : displayedImage ? (
          <div className="w-fit max-w-full overflow-hidden rounded-md border border-dashed border-dark-border-solid bg-dark-base-dark p-2">
            <Input
              {...baseProps}
              placeholder={getPlaceholder(block.title)}
              disabled={disabled}
              className={clsx(
                baseInputClassName,
                '!h-full absolute inset-0 user-select-none pointer-events-none !bg-transparent !border-none'
              )}
              onChange={handleChange}
              accept={acceptedMimeTypes}
              ref={inputRef}
              {...props}
            />
            <div
              style={{
                aspectRatio: displayedImage.aspectRatio,
                width: displayedImage.width,
                maxWidth: '100%',
              }}
              className="relative"
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={displayedImage.src}
                alt=""
                loading="lazy"
                decoding="async"
                style={{
                  opacity: isLoading ? 0.3 : 1,
                  backgroundImage: `url(data:image/png;base64,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)`,
                  backgroundRepeat: 'repeat',
                  backgroundSize: '16px',
                }}
                className="w-full"
                onDoubleClick={() => {
                  inputRef.current?.click()
                }}
              />
            </div>
          </div>
        ) : (
          <>
            <Input
              {...baseProps}
              placeholder={getPlaceholder(block.title)}
              className={clsx(
                // IMPORTANT! z-index: 30 is important to have a seamless UX when dragging files to the editor
                '!h-36 relative z-30',
                baseInputClassName,
                isDragging ? '!bg-dark-shade-hover' : '!bg-dark-base-dark'
              )}
              onChange={handleChange}
              disabled={disabled}
              onDragEnter={(e) => {
                e.stopPropagation()
                if (!e.dataTransfer.types.includes('Files')) return
                setIsDragging(true)
              }}
              onDragLeave={(e) => {
                e.stopPropagation()
                setIsDragging(false)
              }}
              onDrop={() => setIsDragging(false)}
              accept={acceptedMimeTypes}
              ref={inputRef}
              {...props}
            />
            <div className="flex flex-col pointer-events-none absolute top-1/2 w-full -translate-y-1/2 text-center text-sm leading-normal text-dark-dim z-30">
              {value.status === 'uploading' ? (
                <span className="text-xs">
                  Uploading... Wait for upload to finish to interact.
                </span>
              ) : isDragging ? (
                <span className="text-xs">Drop to upload</span>
              ) : (
                <>
                  Upload or drop a new file
                  <span className="text-dark-faint text-xs">Max size: 1GB</span>
                  <div className="flex gap-x-2 mt-4 mx-auto">
                    <Button
                      intent="secondary"
                      icon={<UploadIcon height={15} width={15} />}
                    >
                      Upload new file
                    </Button>
                    <SelectMediaDialog
                      onUpload={() => {
                        inputRef.current?.click()
                      }}
                      onSelect={async (selectedFile) => {
                        try {
                          const response = await fetch(selectedFile.url)
                          const blob = await response.blob()
                          const fileName =
                            selectedFile.key.split('/').pop() || ''
                          const mimeType = blob.type
                          const fileSize = blob.size
                          const lastModified = new Date().getTime()

                          const baseProps = {
                            status: 'uploaded' as const,
                            fileName,
                            url: selectedFile.url,
                            mimeType,
                            fileSize,
                            lastModified,
                          }

                          let value: MediaBlock['value']
                          switch (selectedFile.type) {
                            case 'image':
                              value = {
                                type: 'image',
                                ...baseProps,
                                width: selectedFile.width,
                                height: selectedFile.height,
                                altText: selectedFile.altText,
                              }
                              break
                            case 'video':
                              value = {
                                type: 'video',
                                ...baseProps,
                                width: selectedFile.width,
                                height: selectedFile.height,
                                duration: selectedFile.duration,
                              }
                              break
                            case 'audio':
                              value = {
                                type: 'audio',
                                ...baseProps,
                                duration: selectedFile.duration,
                              }
                              break
                            case 'file':
                              value = {
                                type: 'file',
                                ...baseProps,
                              }
                              break
                          }
                          await replicache?.mutate.updateBlock({
                            id: block.id,
                            strategy: 'merge',
                            instanceChildTarget: block.instanceChildTarget,
                            path,
                            isoNow: new Date().toISOString(),
                            data: {
                              value,
                              type: 'media',
                            },
                          })
                        } catch (err) {
                          console.error(err)
                        }
                      }}
                      constraints={block.constraints.allowedMimeTypes}
                    >
                      <Button
                        intent="secondary"
                        onClick={(e) => {
                          e.stopPropagation()
                        }}
                        className="pointer-events-auto"
                        icon={<MagnifyingGlassIcon height={15} width={15} />}
                      >
                        Select from this repo
                      </Button>
                    </SelectMediaDialog>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
      <video ref={videoElementRef} className="hidden" />
    </>
  )
}

export const FileLoadingIndicator = ({
  label = 'Uploading',
}: {
  label?: string
}) => {
  return (
    <span className="inline-flex items-center space-x-2 text-xs">
      <LoadingIcon status={'loading'} />
      <span>{label}</span>
    </span>
  )
}
